/**
 * Манифест звуков (`public/audio/manifest.json`, формат — `public/audio/README.md`) и чистая логика: какие звуки
 * запускает событие боя, какую музыку включить, какой формат файлов выбрать. Без Web Audio — всё проверяется юнитами.
 */
import type { BattleEvent } from "@newfungame/contracts";

export type AudioFormat = "ogg" | "m4a";

export interface SoundRule {
  type: BattleEvent["type"];
  /** Равенство полей события. */
  where?: Record<string, string | number | boolean>;
}

export interface SoundDef {
  kind: "sfx" | "music";
  description?: string;
  /** Базовая громкость, 0..1. */
  volume: number;
  /** Варианты одного звука: при запуске выбирается не тот, что звучал только что. */
  variants: Record<AudioFormat, string>[];
  preload: boolean;
  /** Сколько копий звучит одновременно (по умолчанию `DEFAULT_MAX_VOICES`). */
  voices?: number;
  /** Наименьший промежуток между запусками, мс: частые звуки не сливаются в трещотку. */
  gapMs?: number;
  loop?: boolean;
  /** Границы петли в файле, с: вокруг них запас под тишину, которую добавляют кодеки. */
  loopStart?: number;
  loopEnd?: number;
  events: SoundRule[];
}

export interface AudioManifest {
  version: number;
  basePath: string;
  formats: AudioFormat[];
  sounds: Record<string, SoundDef>;
}

export type MusicAction = { play: string } | { stop: true };

export function matchesRule(rule: SoundRule, event: BattleEvent): boolean {
  if (rule.type !== event.type) return false;
  if (!rule.where) return true;
  const fields = event as unknown as Record<string, unknown>;
  return Object.entries(rule.where).every(([k, v]) => fields[k] === v);
}

function idsFor(manifest: AudioManifest, kind: SoundDef["kind"], event: BattleEvent): string[] {
  return Object.entries(manifest.sounds)
    .filter(([, s]) => s.kind === kind && s.events.some((r) => matchesRule(r, event)))
    .map(([id]) => id);
}

/** Эффекты (не музыка) для события. `skillCast` — общий звук навыка плюс выстрел героя. */
export function soundsForEvent(manifest: AudioManifest, event: BattleEvent): string[] {
  const own = idsFor(manifest, "sfx", event);
  if (event.type !== "skillCast") return own;
  const shot = { type: "shot", owner: event.heroId, projectileId: 0 } as unknown as BattleEvent;
  return [...own, ...idsFor(manifest, "sfx", shot)];
}

/** Эффекты тика: каждый звук не больше раза за тик (дедуп), в порядке появления событий. */
export function planTick(manifest: AudioManifest, events: readonly BattleEvent[]): string[] {
  const out = new Set<string>();
  for (const e of events) for (const id of soundsForEvent(manifest, e)) out.add(id);
  return [...out];
}

/** Музыка по событию: `waveStarted` — петля из манифеста, `waveCleared`/`waveLost` — стоп. */
export function musicAction(manifest: AudioManifest, event: BattleEvent): MusicAction | null {
  if (event.type === "waveCleared" || event.type === "waveLost") return { stop: true };
  const [id] = idsFor(manifest, "music", event);
  return id ? { play: id } : null;
}

/** ogg (Opus) где поддерживается, иначе m4a (iOS/Safari не играют Opus в ogg). */
export function pickFormat(probe: { canPlayType(type: string): string } | null | undefined): AudioFormat {
  try {
    if (probe && probe.canPlayType('audio/ogg; codecs="opus"') !== "") return "ogg";
  } catch {
    /* нет аудио-элемента — m4a */
  }
  return "m4a";
}

export function soundUrl(manifest: AudioManifest, id: string, format: AudioFormat, variant = 0): string {
  const file = manifest.sounds[id]?.variants[variant];
  if (!file) throw new Error(`unknown sound: ${id}#${variant}`);
  return manifest.basePath + file[format];
}

/** Случайный вариант, кроме `last`: два одинаковых подряд заметнее всего. */
export function pickVariant(count: number, last: number | undefined, random: () => number): number {
  if (count < 2) return 0;
  if (last === undefined) return Math.floor(random() * count);
  return (last + 1 + Math.floor(random() * (count - 1))) % count;
}

/** Сколько копий одного звука могут звучать одновременно, если манифест не сказал иначе. */
export const DEFAULT_MAX_VOICES = 3;

/** Счётчик голосов: не даёт запустить больше N одинаковых звуков одновременно. */
export class VoiceLimiter {
  private readonly active = new Map<string, number>();
  constructor(private readonly max: (id: string) => number = () => DEFAULT_MAX_VOICES) {}
  acquire(id: string): boolean {
    const n = this.active.get(id) ?? 0;
    if (n >= this.max(id)) return false;
    this.active.set(id, n + 1);
    return true;
  }
  release(id: string): void {
    const n = this.active.get(id) ?? 0;
    if (n <= 1) this.active.delete(id);
    else this.active.set(id, n - 1);
  }
  count(id: string): number {
    return this.active.get(id) ?? 0;
  }
}
